import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { caseAPI, paymentAPI, authAPI, settingsAPI } from '../api';
import Logo from '../components/Logo';

export default function Dashboard() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [receiptNumber, setReceiptNumber] = useState('');
  const [nickname, setNickname] = useState('');
  const [error, setError] = useState('');
  const [checkingOut, setCheckingOut] = useState(false);
  const [trackingFrequency, setTrackingFrequency] = useState('daily');
  const [updatingFrequency, setUpdatingFrequency] = useState(false);
  const [managingBilling, setManagingBilling] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, refreshUser, isAuthenticated, loading: authLoading } = useAuth();

  // CRITICAL: Clear all data if auth becomes invalid
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      setCases([]);
      setLoading(false);
    }
  }, [isAuthenticated, authLoading]);

  useEffect(() => {
    if (authLoading) {
      return;
    }
    
    if (isAuthenticated && user) {
      loadData();
    } else {
      setLoading(false);
    }
    // loadData is intentionally excluded from deps to avoid infinite loops
  }, [authLoading, isAuthenticated, user]);

  useEffect(() => {
    // Auto-trigger checkout if user just logged in and needs subscription
    if (user && user.subscription_status !== 'active') {
      const intent = localStorage.getItem('payment_intent');
      if (intent === 'subscribe') {
        localStorage.removeItem('payment_intent');
        setTimeout(() => handleUpgrade(), 500);
      }
    }
  }, [user]);

  const loadData = async () => {
    if (!isAuthenticated || !user) {
      setLoading(false);
      return;
    }
    
    try {
      if (user.subscription_status === 'active') {
        try {
          const casesRes = await caseAPI.getTrackedCases();
          setCases(casesRes.data);
          
          const frequencyRes = await settingsAPI.getTrackingFrequency();
          setTrackingFrequency(frequencyRes.data.tracking_frequency);
        } catch (err) {
          if (err.response?.status !== 403) {
            // Log non-403 errors for debugging
            if (process.env.NODE_ENV === 'development') {
              // Only log in development
            }
          }
        }
      }
    } catch (err) {
      // Log critical data loading errors
      if (process.env.NODE_ENV === 'development') {
        // Development-only logging
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddCase = async (e) => {
    e.preventDefault();
    setError('');
    setAdding(true);

    try {
      await caseAPI.addTrackedCase(receiptNumber, nickname || null);
      setReceiptNumber('');
      setNickname('');
      setShowAddForm(false);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add case');
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteCase = async (caseId) => {
    if (!window.confirm('Are you sure you want to remove this case?')) return;

    try {
      await caseAPI.deleteCase(caseId);
      await loadData();
    } catch (err) {
      alert('Failed to remove case');
    }
  };

  const handleUpgrade = async () => {
    setCheckingOut(true);
    setError(''); // Clear any previous errors
    try {
      const origin_url = window.location.origin;
      const response = await paymentAPI.createCheckout(origin_url);
      window.location.href = response.data.url;
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to start checkout';
      setError(errorMsg);
      alert(`Checkout failed: ${errorMsg}`);
      setCheckingOut(false);
    }
  };

  const handleAddCaseClick = () => {
    if (user?.subscription_status !== 'active') {
      // Free user clicked Add Case - redirect to checkout
      handleUpgrade();
    } else if (cases.length >= 5) {
      // Already at limit - show error message
      setError('You have reached the maximum limit of 5 tracked cases. Please delete a case before adding a new one.');
    } else {
      // Paid user - show add form
      setShowAddForm(true);
      setError(''); // Clear any previous errors
    }
  };

  const handleManageBilling = async () => {
    setManagingBilling(true);
    try {
      const origin_url = window.location.origin;
      const return_url = `${origin_url}/dashboard`;
      const response = await paymentAPI.createBillingPortal(origin_url, return_url);
      window.location.href = response.data.url;
    } catch (err) {
      // Show specific error message from backend
      const errorMessage = err.response?.data?.detail || 'Failed to open billing portal. Please try again.';
      alert(errorMessage);
      setManagingBilling(false);
    }
  };

  const handleFrequencyChange = async (newFrequency) => {
    setUpdatingFrequency(true);
    try {
      await settingsAPI.updateTrackingFrequency(newFrequency);
      setTrackingFrequency(newFrequency);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update tracking frequency');
    } finally {
      setUpdatingFrequency(false);
    }
  };

  const getFrequencyLabel = (freq) => {
    const labels = {
      '6h': 'Every 6 hours',
      '12h': 'Every 12 hours',
      'daily': 'Daily',
      '2days': 'Every 2 days',
      'weekly': 'Weekly'
    };
    return labels[freq] || freq;
  };

  // Show loading only for Dashboard data, not auth initialization
  // ProtectedRoute already handles auth checks
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Derive subscription status dynamically on every render
  const isSubscribed = user?.subscription_status === 'active';

  // CRITICAL GUARD: If not authenticated after loading, show NOTHING
  // ProtectedRoute will handle redirect, this prevents UI flash
  if (!authLoading && !isAuthenticated) {
    return null;
  }

  // CRITICAL: If user exists but subscription_status is undefined, keep loading
  // This prevents showing free UI while user data is incomplete
  if (isAuthenticated && user && user.subscription_status === undefined) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading subscription data...</p>
          <p className="mt-2 text-xs text-gray-500">If this persists, refresh the page or contact support</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex justify-between items-center">
            <Logo />
            <nav className="flex items-center gap-4">
              <span className="text-sm text-gray-500 hidden sm:block truncate max-w-[200px]">{user?.email}</span>
              <button
                onClick={logout}
                className="text-sm font-medium text-gray-600 hover:text-gray-900"
                data-testid="logout-button"
              >
                Logout
              </button>
            </nav>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Free User - Upgrade Banner */}
        {!isSubscribed && (
          <div className="mb-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                <p className="font-semibold">Checkout Error:</p>
                <p className="text-sm">{error}</p>
              </div>
            )}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg p-6 text-white" data-testid="subscription-banner">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="mb-4 md:mb-0">
                <h3 className="text-2xl font-bold mb-2">Start tracking your USCIS cases</h3>
                <p className="text-indigo-100 mb-3">
                  Subscribe to add and track USCIS cases, receive alerts, and view history.
                </p>
                <div className="flex flex-wrap gap-2 text-sm">
                  <span className="bg-white/20 px-3 py-1 rounded-full">✓ Track up to 5 cases</span>
                  <span className="bg-white/20 px-3 py-1 rounded-full">✓ Daily checks</span>
                  <span className="bg-white/20 px-3 py-1 rounded-full">✓ Email alerts</span>
                </div>
              </div>
              <button
                onClick={handleUpgrade}
                disabled={checkingOut}
                className="bg-white text-indigo-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 whitespace-nowrap disabled:opacity-50"
                data-testid="upgrade-button"
              >
                {checkingOut ? 'Loading...' : 'Start tracking for $9/month'}
              </button>
            </div>
          </div>
          </div>
        )}

        {/* Dashboard Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="dashboard-title">
              {isSubscribed ? 'My Cases' : 'Dashboard'}
            </h1>
            <p className="text-gray-600">
              {isSubscribed 
                ? `Tracking ${cases.length} of 5 cases`
                : 'Subscribe to start tracking your USCIS cases'}
            </p>
          </div>

          {/* Add Case Button */}
          <button
            onClick={handleAddCaseClick}
            disabled={isSubscribed && cases.length >= 5}
            className={`px-6 py-3 rounded-lg font-semibold flex items-center space-x-2 transition-colors ${
              isSubscribed && cases.length >= 5
                ? 'bg-gray-400 text-white cursor-not-allowed opacity-60'
                : isSubscribed
                ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                : 'bg-gray-300 text-gray-600 cursor-pointer hover:bg-gray-400'
            }`}
            data-testid="add-case-button"
            title={isSubscribed && cases.length >= 5 ? 'Maximum 5 cases reached. Delete a case to add a new one.' : ''}
          >
            {!isSubscribed && (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            )}
            <span>
              {isSubscribed && cases.length >= 5 
                ? '5/5 Cases (Max)' 
                : isSubscribed 
                ? '+ Add Case' 
                : 'Add Case (Subscribe)'}
            </span>
          </button>
        </div>

        {/* Error Message for Subscribed Users */}
        {isSubscribed && error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            <p className="text-sm">{error}</p>
          </div>
        )}

        {/* Add Case Form (for paid users) */}
        {isSubscribed && showAddForm && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h3 className="text-lg font-semibold mb-4">Add New Case</h3>
            <form onSubmit={handleAddCase} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Receipt Number *
                </label>
                <input
                  type="text"
                  value={receiptNumber}
                  onChange={(e) => setReceiptNumber(e.target.value.toUpperCase())}
                  placeholder="e.g., IOE0912345678"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  required
                  data-testid="add-receipt-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nickname (optional)
                </label>
                <input
                  type="text"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  placeholder="e.g., My Green Card"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  data-testid="add-nickname-input"
                />
              </div>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}
              <div className="flex space-x-4">
                <button
                  type="submit"
                  disabled={adding}
                  className="bg-indigo-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50"
                  data-testid="submit-add-case"
                >
                  {adding ? 'Adding...' : 'Add Case'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setError('');
                  }}
                  className="bg-gray-200 text-gray-900 px-6 py-2 rounded-lg font-semibold hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Subscription Management & Settings */}
        {isSubscribed && (
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Subscription Section */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Subscription</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Plan</span>
                  <span className="font-semibold text-indigo-600">Pro</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Status</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    user?.subscription_status === 'active' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {user?.subscription_status || 'inactive'}
                  </span>
                </div>
                <div className="pt-3 border-t border-gray-200">
                  <button
                    onClick={handleManageBilling}
                    disabled={managingBilling}
                    className="w-full bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                  >
                    {managingBilling ? 'Loading...' : 'Manage Subscription'}
                  </button>
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    Cancel, update payment, or view billing
                  </p>
                </div>
              </div>
            </div>

            {/* Tracking Frequency Section */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Tracking Frequency</h3>
              <p className="text-sm text-gray-600 mb-4">
                Choose how often we check your cases for updates
              </p>
              <div className="space-y-2">
                {['6h', '12h', 'daily', '2days', 'weekly'].map((freq) => (
                  <label
                    key={freq}
                    className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors ${
                      trackingFrequency === freq 
                        ? 'border-indigo-600 bg-indigo-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="frequency"
                        value={freq}
                        checked={trackingFrequency === freq}
                        onChange={(e) => handleFrequencyChange(e.target.value)}
                        disabled={updatingFrequency}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="ml-3 text-sm font-medium text-gray-900">
                        {getFrequencyLabel(freq)}
                      </span>
                    </div>
                    {trackingFrequency === freq && (
                      <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    )}
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Cases List or Empty State */}
        {cases.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <div className="text-gray-400 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {isSubscribed ? 'No cases tracked yet' : 'You need an active subscription to track cases'}
            </h3>
            <p className="text-gray-600 mb-6">
              {isSubscribed 
                ? 'Click "Add Case" above to start tracking your USCIS cases.'
                : 'Subscribe to USCIS Tracker Pro to add cases, get daily checks, and receive email alerts.'}
            </p>
            {!isSubscribed && (
              <button
                onClick={handleUpgrade}
                disabled={checkingOut}
                className="bg-indigo-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50"
              >
                {checkingOut ? 'Loading...' : 'Start tracking for $9/month'}
              </button>
            )}
          </div>
        ) : (
          <div className="grid gap-6" data-testid="cases-list">
            {cases.map((caseItem) => (
              <div
                key={caseItem.id}
                className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  navigate(`/case/${caseItem.id}`);
                }}
                data-testid={`case-item-${caseItem.receipt_number}`}
              >
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {caseItem.nickname || caseItem.receipt_number}
                      </h3>
                      <p className="text-sm text-gray-500">{caseItem.receipt_number}</p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCase(caseItem.id);
                      }}
                      className="text-red-600 hover:text-red-700 text-sm"
                      data-testid={`delete-case-${caseItem.receipt_number}`}
                    >
                      Remove
                    </button>
                  </div>

                  {caseItem.latest_status && (
                    <div>
                      <div className="mb-3">
                        <h4 className="font-semibold text-gray-900 mb-1">
                          {caseItem.latest_status.title}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {caseItem.latest_status.ai_summary}
                        </p>
                      </div>
                      <div className="flex items-center space-x-4 text-sm">
                        <span className="text-gray-500">
                          Phase: <span className="font-medium">{caseItem.latest_status.estimated_phase}</span>
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          caseItem.latest_status.urgency_level === 'high' ? 'bg-red-100 text-red-800' :
                          caseItem.latest_status.urgency_level === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {caseItem.latest_status.urgency_level} urgency
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Mobile Bottom CTA for Free Users */}
      {!isSubscribed && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg">
          <button
            onClick={handleUpgrade}
            disabled={checkingOut}
            className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50"
          >
            {checkingOut ? 'Loading...' : 'Start tracking for $9/month'}
          </button>
        </div>
      )}
    </div>
  );
}
